import { useState, useEffect, useContext } from "react";

import MUILoadingButton from "@mui/lab/LoadingButton";
import MUIButton from "@mui/material/Button";
import MUIDialogActions from "@mui/material/DialogActions";
import MUITypography from "@mui/material/Typography";
import MUIBox from "@mui/material/Box";
import MUIAutocomplete from "@mui/material/Autocomplete";
import MUITextField from "@mui/material/TextField";

import Dialog from "../DialogFunctional";
import MainContext from "../../Context/MainContext/MainContext";
import User from "../../Utility/Crud/User";
import api from "../../Seating/Security/api";
import { enqueueSnackbar as NotistackEnqueueSnackbar } from "notistack";

export default function DialogConfirmMoveAccounts({
  open,
  onClose,
  selectedAccounts,
}) {
  const mainContext = useContext(MainContext);
  const [selectedSalesRep, setSelectedSalesRep] = useState(null);
  const [error, setError] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const selectedAccountsList = Object.values(selectedAccounts) || [];

  useEffect(() => {
    setSelectedSalesRep(null);
    setError({});
    setIsLoading(false);
  }, [onClose]);

  /**
   * Builds dialog data for reopening a ticket.
   * @returns {Object} Dialog data object for reopening.
   */
  function getMoveAccountsDialogData() {
    const actions = {
      cancelText: `Cancel`,
      confirmText: `Move ${selectedAccountsList.length} Account${selectedAccountsList.length !== 1 ? "s" : ""}`,
      onClick: handleConfirmMoveAccounts,
    };

    return {
      title: `Move Accounts`,
      description: ``,
      content: [<MUIBox key="salesRep">{renderSalesRepOptions()}</MUIBox>],
      actions,
    };
  }

  /**
   * Handles confirming moving accounts.
   * @returns {void}
   */
  function handleConfirmMoveAccounts() {
    setIsLoading(true);
    if (selectedSalesRep) {
      moveSelectedAccounts();
    } else {
      setError({ noSelectedSalesRep: true });
    }
    setIsLoading(false);
  }

  /**
   * Moves the selected accounts to the new sales rep.
   * @returns {void}
   */
  async function moveSelectedAccounts() {
    const accounts = selectedAccountsList.map((account) => account.id);
    await api
      .send("POST", "/moveAccounts", {
        accounts: accounts,
        newOwnerId: selectedSalesRep.id || null,
      })
      .then((response) => {
        NotistackEnqueueSnackbar("Accounts moved successfully.", {
          variant: "success",
        });
      })
      .catch((error) => {
        NotistackEnqueueSnackbar("Accounts move failed.", { variant: "error" });
      });
    setIsLoading(false);
    onClose({}, true);
  }

  /**
   * Renders the autocomplete field for reasons for cancellation.
   * @returns {JSX.Element}
   */
  const renderSalesRepOptions = () => {
    const salesRepOptions = User.getSalesReps(mainContext.usersIndexed);

    return (
      <MUIAutocomplete
        options={salesRepOptions}
        value={selectedSalesRep}
        isOptionEqualToValue={(option, value) => option?.id === value?.id}
        getOptionKey={(option) => option?.id || ""}
        getOptionLabel={(option) => User.getDisplayName(option) || ""}
        onChange={(e, newValue) => {
          setSelectedSalesRep(newValue);
        }}
        renderInput={(params) => (
          <MUITextField
            {...params}
            InputLabelProps={{ shrink: true }}
            required={true}
            label="Sales Representative"
            error={error.noSelectedSalesRep ? true : false}
            helperText={
              error.noSelectedSalesRep
                ? "Sales Representative is required."
                : ""
            }
            size="small"
            variant="standard"
            fullWidth
            sx={{ mb: 2 }}
          />
        )}
      />
    );
  };

  /**
   * Render header title of dialog
   */
  function renderHeader() {
    const dialogData = getMoveAccountsDialogData();
    return (
      <MUITypography variant="h6" color="textPrimary">
        {dialogData.title}
      </MUITypography>
    );
  }

  /**
   * Render main content text of dialog
   */
  function renderContent() {
    const dialogData = getMoveAccountsDialogData();
    return (
      <>
        <MUITypography sx={{ mb: 2 }} variant="body1" color="textPrimary">
          {dialogData.description}
        </MUITypography>
        <MUIBox>{dialogData.content}</MUIBox>
      </>
    );
  }

  /**
   * Render cancel and confirm button actions of dialog.
   */
  function renderActions() {
    const dialogData = getMoveAccountsDialogData();
    const { cancelText, confirmText, onClick } = dialogData.actions;
    return (
      <MUIDialogActions spacing={2}>
        {cancelText && (
          <MUIButton
            variant="text"
            onClick={() => onClose(selectedAccounts, false)}
          >
            {cancelText}
          </MUIButton>
        )}
        <MUILoadingButton
          variant="contained"
          loading={isLoading}
          onClick={onClick}
        >
          {confirmText || "Confirm"}
        </MUILoadingButton>
      </MUIDialogActions>
    );
  }

  /**
   * Gets the max width of the dialog.
   * @returns {string}
   */
  function getMaxWidth() {
    return "md";
  }

  return (
    <Dialog
      renderHeader={renderHeader}
      renderContent={renderContent}
      renderActions={renderActions}
      maxWidth={getMaxWidth}
      open={open}
      onClose={() => onClose(selectedAccounts, false)}
    />
  );
}
